import React, { useEffect, useState, useContext } from "react";
import apiClient from "../../api/client";
import { AuthContext } from "../../App";

interface EarningsSummary {
  total_cents: number;
  pending_hold_cents: number;
  available_cents: number;
  paid_cents: number;
}

const formatDollars = (cents: number) =>
  (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

export const LoveLinkEarningsTeaser: React.FC = () => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState<EarningsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      // Don't fetch if no token
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const res = await apiClient.get("/referrals/earnings/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setData(res.data);
        }
      } catch (e: any) {
        // Silence 403/401 errors - user may not have referral access
        const status = e?.response?.status;
        if (status === 403 || status === 401) {
          console.info('[LoveLink] Earnings not available for this user (expected)');
        }
        if (!cancelled) {
          setError("Unable to load LoveLink earnings right now.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Stay invisible while loading or if API fails
  // Also hide when user has $0 earned - no point showing zero-state
  if (loading || error || !data) return null;
  if (!data.total_cents || data.total_cents === 0) return null;

  const total = formatDollars(data.total_cents);
  const available = formatDollars(data.available_cents);

  return (
    <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-900">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-emerald-900">
            LoveLink Rewards
          </div>
          <div className="mt-0.5 text-xs text-emerald-800">
            You've received{" "}
            <span className="font-semibold">{total}</span>{" "}
            from your Circle so far.{" "}
            <span className="font-semibold">{available}</span> is on track
            to become available after the 30-day protection window.
          </div>
        </div>
        <a
          href="/lovelink"
          className="mt-0.5 text-xs font-semibold text-emerald-800 underline-offset-2 hover:underline"
        >
          View details →
        </a>
      </div>
    </div>
  );
};
